import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnchorId } from '../core/Enums';
import { PaymentsSubsceneId } from '../core/PaymentsEnums';
import { ChevronDownIcon, SuccessCheckmarkIcon } from '../icons/TourIcons';
import { useTourAnchor } from '../hooks/useTourAnchor';
import { useTour } from '../core/TourProvider';
import styles from '../stylesheets/tenantPaymentReview.module.scss';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);

interface TenantPaymentReviewProps {
  onSubmit?: () => void;
}

const TenantPaymentReview = ({ onSubmit }: TenantPaymentReviewProps) => {
  const { model, setSubscene, currentSubscene } = useTour();
  const totalAccordionRef = useTourAnchor(
    AnchorId.TENANT_REVIEW_TOTAL_ACCORDION
  );
  const invoiceAccordionRef = useTourAnchor(
    AnchorId.TENANT_REVIEW_INVOICE_ACCORDION
  );
  const accordionListRef = useTourAnchor(AnchorId.TENANT_REVIEW_ACCORDION_LIST);
  const payNowRef = useTourAnchor(AnchorId.TENANT_REVIEW_PAY_NOW);
  const successContinueRef = useTourAnchor(
    AnchorId.TENANT_REVIEW_SUCCESS_CONTINUE
  );
  const processingRef = useTourAnchor(AnchorId.TENANT_REVIEW_PROCESSING);
  const [totalExpanded, setTotalExpanded] = useState(true);
  const [methodExpanded, setMethodExpanded] = useState(true);
  const [shouldAutoAdvanceToSuccess, setShouldAutoAdvanceToSuccess] =
    useState(false);
  const processingTimeoutRef = useRef<number | null>(null);
  const invoices = model.payments?.invoices ?? [];
  const outstandingInvoice =
    invoices.find((invoice) => invoice.status === 'outstanding') ?? invoices[0];
  const currentSubsceneId = currentSubscene?.id ?? PaymentsSubsceneId.REVIEW;
  const isProcessing = currentSubsceneId === PaymentsSubsceneId.PROCESSING;
  const isSuccess = currentSubsceneId === PaymentsSubsceneId.SUCCESS;

  const lineItemsTotal = useMemo(() => {
    if (!outstandingInvoice) {
      return 0;
    }
    return outstandingInvoice.lineItems.reduce(
      (total, item) => total + item.amount * item.quantity,
      0
    );
  }, [outstandingInvoice]);

  const invoiceLabel = outstandingInvoice.docNo ?? outstandingInvoice.id;
  const paymentEnding = String(invoiceLabel).slice(-4);
  const portalFee = Math.max(outstandingInvoice.balance - lineItemsTotal, 0);
  const totalAmount = lineItemsTotal + portalFee;
  const invoiceSite =
    outstandingInvoice.lineItems[0]?.site ?? model.tenant.unitName;
  const invoiceName = `${invoiceSite} - ${outstandingInvoice.issuedDate}`;

  const handlePayNow = () => {
    if (processingTimeoutRef.current) {
      window.clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
    setShouldAutoAdvanceToSuccess(true);
    setSubscene(PaymentsSubsceneId.PROCESSING);
  };

  useEffect(() => {
    if (!isProcessing) {
      if (processingTimeoutRef.current) {
        window.clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
      setShouldAutoAdvanceToSuccess(false);
      return;
    }
    if (!shouldAutoAdvanceToSuccess || processingTimeoutRef.current) {
      return;
    }
    processingTimeoutRef.current = window.setTimeout(() => {
      setSubscene(PaymentsSubsceneId.SUCCESS, { history: 'replace' });
      processingTimeoutRef.current = null;
      setShouldAutoAdvanceToSuccess(false);
    }, 2000);
  }, [isProcessing, setSubscene, shouldAutoAdvanceToSuccess]);

  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        window.clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
    };
  }, []);

  if (isProcessing) {
    return (
      <div className={styles.processingPage} ref={processingRef}>
        <div className={styles.spinner} />
        <h2 className={styles.processingTitle}>Processing payment</h2>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className={styles.successPage}>
        <div className={styles.successIcon}>
          <SuccessCheckmarkIcon />
        </div>
        <h2 className={styles.successTitle}>Payment successful</h2>
        <button
          type="button"
          className={styles.successButton}
          onClick={onSubmit}
          ref={successContinueRef}
        >
          Go back to property manager view
          <span className={styles.successButtonIcon}>
            <ChevronDownIcon />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h2 className={styles.title}>Review & Pay</h2>
      </header>

      <div className={styles.accordionList} ref={accordionListRef}>
        <section className={styles.accordion} ref={invoiceAccordionRef}>
          <button
            type="button"
            className={styles.accordionHeader}
            onClick={() => setTotalExpanded((prev) => !prev)}
            aria-expanded={totalExpanded}
            ref={totalAccordionRef}
          >
            <div className={styles.accordionHeaderLeft}>
              <span className={styles.accordionTitle}>Total:</span>
            </div>
            <div className={styles.accordionHeaderRight}>
              <span className={styles.accordionAmount}>
                {formatCurrency(totalAmount)}
              </span>
              <span
                className={
                  totalExpanded
                    ? styles.chevronExpanded
                    : styles.chevronCollapsed
                }
              >
                <ChevronDownIcon />
              </span>
            </div>
          </button>
          {totalExpanded && (
            <div className={styles.accordionBody}>
              <div className={styles.invoiceRow}>
                <div>
                  <div className={styles.invoiceName}>{invoiceName}</div>
                </div>
                <div className={styles.amount}>
                  {formatCurrency(outstandingInvoice.balance)}
                </div>
              </div>
              {portalFee > 0 && (
                <div className={styles.invoiceRow}>
                  <div className={styles.invoiceMeta}>Portal fee</div>
                  <div className={styles.amount}>
                    {formatCurrency(portalFee)}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <section className={styles.accordion}>
          <button
            type="button"
            className={styles.accordionHeader}
            onClick={() => setMethodExpanded((prev) => !prev)}
            aria-expanded={methodExpanded}
          >
            <div className={styles.accordionHeaderLeft}>
              <span className={styles.accordionTitle}>Payment Method</span>
            </div>
            <div className={styles.accordionHeaderRight}>
              <span className={styles.accordionSummary}>
                ACH •••• {paymentEnding}
              </span>
              <span
                className={
                  methodExpanded
                    ? styles.chevronExpanded
                    : styles.chevronCollapsed
                }
              >
                <ChevronDownIcon />
              </span>
            </div>
          </button>
          {methodExpanded && (
            <div className={styles.accordionBody}>
              <div className={styles.methodRow}>
                <span className={styles.methodRadio} aria-hidden="true" />
                <div className={styles.methodInfo}>
                  <div className={styles.methodName}>
                    ACH •••• {paymentEnding}
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      <button
        type="button"
        className={styles.submitButton}
        onClick={handlePayNow}
        ref={payNowRef}
      >
        Pay Now <i>(*Simulated Payment*)</i>
      </button>

      <div className={styles.carbonMessage}>
        <span className={styles.carbonDot} />
        Every payment removes carbon from the air - at no extra cost.
      </div>
    </div>
  );
};

export default TenantPaymentReview;
